import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import { getPetAssetKey, speciesAssetsRatio, speciesChatbot } from './species';
import { buildSender } from '../sender';

/**
 * Assets for revived messages
 */
export const revivedAssets = {
  'background/revived': 'private/chatbot/visit/friendvisit_revived_bg.png',
  'text/revived_en': 'private/chatbot/visit/revived_en.png',
  'text/revived_ja': 'private/chatbot/visit/revived_ja.png',
};

/**
 * Create revived message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const revivedMessage = createFlexMessageTemplate(
  SB.object({
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja']),
    petName: SB.string(),
    visitor: SB.object({
      name: SB.string(),
      photo: SB.string(),
      pet: SB.tuple(speciesChatbot),
    }),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).visit.finish.text.replace(
      '{name}',
      data.visitor.name,
    );
    const deadPetKey = getPetAssetKey(data.species, 'dead');
    const deadPet = api.getAssetPath(deadPetKey);
    const normalPetKey = getPetAssetKey(data.species, 'normal');
    const normalPet = api.getAssetPath(normalPetKey);

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('background/revived'),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.3:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(`text/revived_${data.locale}`),
                size: '600px',
                gravity: 'top',
                offsetTop: '-55px',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
          {
            type: 'box',
            layout: 'horizontal',
            position: 'absolute',
            width: '100%',
            alignItems: 'flex-end',
            height: '205px',
            contents: [
              {
                type: 'image',
                url: deadPet,
                aspectRatio: speciesAssetsRatio[deadPetKey],
                size: '80%',
                position: 'relative',
              },
              {
                type: 'image',
                url: normalPet,
                aspectRatio: speciesAssetsRatio[normalPetKey],
                size: '80%',
                position: 'relative',
                offsetTop: '0px',
              },
            ],
            spacing: '60px',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(`button/playnow_${data.locale}`),
                position: 'relative',
                size: '200px',
                offsetBottom: '-50px',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = buildSender({
      name: data.visitor.name,
      iconUrl: data.visitor.photo,
    });

    return template;
  },
);
